import {useEffect, useRef} from "react";
import {KeenIcon} from '@/components';
import {Link} from "react-router-dom";

interface ITaskItem {
    type: string; // Name to identify the step
    status: boolean; // Indicates if the step is completed
    is_next: boolean; // Whether this should be the next visible step
    icon: string;
    title: string;
    text: string;
    button_text: string;
    button_link: string;
    variant: string;
    value: number;
    steps: number;
}

const CardTask = ({
                      type,
                      status,
                      is_next,
                      icon,
                      title,
                      text,
                      button_text,
                      button_link,
                      variant,
                      value,
                      steps,
                  }: ITaskItem) => {
    const cardRef = useRef<HTMLDivElement>(null);

    // Automatically scroll the card into view if it's marked as next
    useEffect(() => {
        if (is_next && cardRef.current) {
            cardRef.current.scrollIntoView({
                behavior: "smooth",
                block: "start",
            });
        }
    }, [is_next]);

    // Determine additional classes and props based on status
    const disabledStyles = !status
        ? "opacity-50 cursor-not-allowed pointer-events-none"
        : "";

    return (
        <div
            ref={cardRef}
            className={`card shadow-none w-[285px] border-0 rounded-xl ${disabledStyles}`}
            data-type={type} // Useful for debugging or analytics
        >
            {/* Content Section */}
            <div className="card-border card-rounded grid gap-6 px-5 pt-3.5 pb-3 mb-4 w-[285px]">
                {/* Header */}
                <div className="flex items-center gap-2.5">
                    <KeenIcon icon={icon} className="default-color text-2xl"/>
                    <div className="grid grid-cols-1 gap-0.5">
                        <a
                            href="#"
                            className={`text-md font-medium mb-px ${status ? "text-gray-900 hover:text-primary-active" : "text-gray-500"}`}
                        >
                            {title}
                        </a>
                    </div>
                </div>

                {/* Text Section */}
                <p className={`text-sm ${status ? "text-gray-700" : "text-gray-500"}`}>
                    {text}
                </p>

                {/* Button Section */}
                <div className="flex items-center gap-2.5">
                    {/* Use a div wrapper when disabled to avoid clickable behavior */}
                    {status ? (
                        <Link
                            to={button_link}
                            className={`btn btn-sm btn-primary flex items-center ${is_next ? "pulsating" : ""}`}
                        >
                            <KeenIcon icon="plus" className="mr-2"/>
                            {button_text}
                        </Link>
                    ) : (
                        <div
                            className={`btn btn-sm btn-primary flex items-center ${is_next ? "pulsating" : ""} opacity-50`}
                            aria-disabled="true"
                        >
                            <KeenIcon icon="plus" className="mr-2"/>
                            {button_text}
                        </div>
                    )}
                </div>

                {/* Progress Bar */}
                <div className="grid gap-1.5 mb-0.5">
                    <div className={`progress h-1.5 ${variant}`}>
                        <div
                            className="progress-bar"
                            style={{width: `${value}%`}}
                        ></div>
                    </div>
                    <div className="flex items-center justify-between">
            <span className="text-gray-600 text-2xs font-medium">
              {steps} Steps
            </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export {CardTask, type ITaskItem};
